/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { DetailsRow, IColumn, IDetailsRowProps, IDetailsRowStyles } from '@fluentui/react/lib/DetailsList';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { RegexEntityCapability } from 'src/core/types/EntityStore';

export type CluEntityRegexInEditModeProps = {
    columns: IColumn[];
    onDismiss: () => void;
    projectCulture?: string;
    rowProps?: IDetailsRowProps;
    projectType?: CluProjectType;
    initialRegex?: RegexEntityCapability;
    onSubmit: (regex: RegexEntityCapability) => void;
};

const messages = defineMessages({
    save: { id: 'clu.cluEntityRegexInEditModeProps.save', defaultMessage: 'Save' },
    cancel: { id: 'clu.cluEntityRegexInEditModeProps.cancel', defaultMessage: 'Cancel' },
    keyPlaceholder: { id: 'clu.cluEntityRegexInEditModeProps.keyPlaceholder', defaultMessage: 'Enter Value' },
    languagePlaceholder: { id: 'clu.cluEntityRegexInEditModeProps.languagePlaceholder', defaultMessage: 'Language' },
    expressionPlaceholder: { id: 'clu.cluEntityRegexInEditModeProps.expressionPlaceholder', defaultMessage: 'Regex Pattern' },
    languageDropDownAriaLabel: { id: 'clu.cluEntityRegexInEditModeProps.languageDropDownAriaLabel', defaultMessage: 'Languages dropDown' }
});

const { styleComponent } = getStylistV2('CluEntityRegexInEditMode');

const scopedTheme = getScopedTheme('CluEntityRegexInEditMode');

const RowContainer = styleComponent<IStackProps>(Stack)(
    'RowContainer',
    (_, cssVars) => ({
        backgroundColor: cssVars.defaultBackground,
        borderBottom: `${scopedTheme.focussingColor} solid 1px`,
        $nest: { '&:hover': { backgroundColor: cssVars.hoveringBackground } }
    }),
    {
        cssVars: { hoveringBackground: '', defaultBackground: '' },
        cssVarsSetter: ({ isSelected, isNewRegex }: { isSelected: boolean; isNewRegex: boolean }) => ({
            hoveringBackground: `${isSelected ? scopedTheme.focussingColor : scopedTheme.hoveringColor}`,
            defaultBackground: `${isNewRegex ? scopedTheme.focussingColor : isSelected ? scopedTheme.hoveringColor : 'unset'}`
        })
    }
);

const detailsRowStyles = classNamesFunction<IDetailsRowProps, IDetailsRowStyles>()({
    cell: { overflow: 'visible' },
    root: { borderBottom: 'none', backgroundColor: 'transparent !important' }
});

/**
 * A component managing and displaying the regex row in both edit and add modes.
 */
export const CluEntityRegexInEditMode = observer((props: CluEntityRegexInEditModeProps) => {
    const { columns, initialRegex, onSubmit, onDismiss, rowProps, projectType, projectCulture = '' } = props;

    const defaultRegex: RegexEntityCapability = { id: '', regexKey: '', language: projectCulture, expression: '' };

    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const [hasRegexDataBeenModified, setHasRegexDataBeenModified] = useState<boolean>(false);
    const [regex, updateRegex] = useUpdateState<RegexEntityCapability>(initialRegex ?? defaultRegex);

    const isSelected = rowProps?.selection.getSelectedIndices().includes(rowProps?.itemIndex);

    const isNewRegex = !initialRegex;

    // TO DO: make validtions
    const isSaveButtonDisabled = !regex.regexKey || !regex.language || !regex.expression || !(isNewRegex || hasRegexDataBeenModified);

    const languageOptions: IDropdownOption[] = (projectType === 'Conversation'
        ? rootStore.cluConversationCultureStoreHouse
        : rootStore.cluOrchestrationCultureStoreHouse
    ).items.map(l => ({
        key: l.code,
        text: l.name,
        disabled: Boolean(regex.language === l.code)
    }));

    // TO DO: add validations
    const onSaveRegex = () => {
        const validatedSublist: RegexEntityCapability = {
            ...regex
        };

        onSubmit(validatedSublist);
    };

    const getColumnContent = (columnKey: string): (() => JSX.Element) => {
        switch (columnKey) {
            case 'regexKey':
                return () => (
                    <LuisTextField
                        value={regex.regexKey}
                        key={`${regex.id}-${columnKey}`}
                        placeholder={localize(messages.keyPlaceholder)}
                        onChange={(_, canonicalForm) => {
                            setHasRegexDataBeenModified(true);
                            updateRegex({ regexKey: canonicalForm });
                        }}
                    />
                );
            case 'language':
                return () => (
                    <Stack tokens={{ childrenGap: 12 }}>
                        <Stack tokens={{ childrenGap: 8 }}>
                            <LuisDropdown
                                required
                                options={languageOptions}
                                selectedKey={regex.language}
                                key={`${regex.id}-${columnKey}-${0}`}
                                placeholder={localize(messages.languagePlaceholder)}
                                ariaLabel={localize(messages.languageDropDownAriaLabel)}
                                onChange={(_, option) => {
                                    updateRegex({
                                        language: option.key as string
                                    });
                                    setHasRegexDataBeenModified(true);
                                }}
                            />
                        </Stack>
                    </Stack>
                );
            case 'expression':
                return () => (
                    <LuisTextField
                        value={regex.expression}
                        key={`${regex.id}-${columnKey}`}
                        placeholder={localize(messages.expressionPlaceholder)}
                        onChange={(_, canonicalForm) => {
                            setHasRegexDataBeenModified(true);
                            updateRegex({ expression: canonicalForm });
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <RowContainer key={`sublist-${regex.id}`} customProps={{ isSelected, isNewRegex }}>
            {isNewRegex ? (
                <Stack horizontal grow tokens={{ padding: '0 0 0 48px' }}>
                    {columns.map(column => (
                        <Stack.Item
                            key={column.key}
                            styles={{
                                root: {
                                    width: column.key === 'expression' ? '150px' : column.calculatedWidth,
                                    padding: '11px 8px 11px 12px'
                                }
                            }}>
                            {getColumnContent(column.key)()}
                        </Stack.Item>
                    ))}
                </Stack>
            ) : (
                <DetailsRow
                    {...rowProps}
                    styles={detailsRowStyles}
                    columns={columns.map(column => ({
                        ...column,
                        onRender: () => (
                            <Stack.Item
                                key={column.key}
                                styles={{
                                    root: {
                                        width: column.key === 'expression' ? '150px' : column.calculatedWidth,
                                        padding: '11px 8px 11px 12px'
                                    }
                                }}>
                                {getColumnContent(column.key)()}
                            </Stack.Item>
                        )
                    }))}
                />
            )}

            <Stack horizontal verticalAlign="center" horizontalAlign="end" tokens={{ childrenGap: 4, padding: 8 }}>
                <PrimaryButton text={localize(messages.save)} disabled={isSaveButtonDisabled} onClick={onSaveRegex} />
                <DefaultButton text={localize(messages.cancel)} onClick={onDismiss} />
            </Stack>
        </RowContainer>
    );
});
